import { FC, ReactNode, useEffect, useRef } from "react";

import { Stack } from "@mui/material";

import { Composer } from "../Composer/Composer";
import { AssistantMessageSource, useAssistantConfig } from "../contexts/AssistantConfigContext";
import type { AssistantChat } from "../helpers/useAssistantChat";
import { Thread } from "../Thread/Thread";

import { useTranslate } from "@/common/hooks/useTranslate";
import { AssistantBubble } from "@/components/AssistantBubble/AssistantBubble";
import { AssistantBubbleText } from "@/components/AssistantBubble/AssistantBubbleText";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export interface DonorAssistantMessageSent {
  turnIndex: number;
  source: "typed" | "suggestion" | "paymentCard";
}

type ChatProps = Pick<
  AssistantChat,
  | "messages"
  | "error"
  | "isBusy"
  | "isComposerDisabled"
  | "canStop"
  | "stop"
  | "input"
  | "setInput"
  | "submit"
  | "answerApproval"
> & {
  suggestions: string[];
  welcome?: { title: string; description: string };
  composerAccessory?: ReactNode;
  errorNotice?: ReactNode;
  isHandedOff?: boolean;
  onStartNew?: () => void;
  canResume?: boolean;
  resume?: () => void;
  onMessageSent?: (params: DonorAssistantMessageSent) => void;
};

export const Chat: FC<ChatProps> = ({
  messages,
  error,
  isBusy,
  isComposerDisabled,
  canStop,
  stop,
  input,
  setInput,
  submit,
  answerApproval,
  suggestions,
  welcome,
  composerAccessory,
  errorNotice,
  isHandedOff,
  onStartNew,
  canResume,
  resume,
  onMessageSent,
}) => {
  const { t } = useTranslate();
  const { feature } = useAssistantConfig();
  const scrollContainer = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const hasSentSuggestion = useRef(false);
  const userTurnCount = messages.filter((message) => message.role === "user").length;
  const reportMessageSent = (source: AssistantMessageSource) => {
    followLatest.current = true;
    onMessageSent?.({ turnIndex: userTurnCount, source });
  };

  useEffect(() => {
    const container = scrollContainer.current;
    if (container && followLatest.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isBusy]);

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {canResume && !isBusy && (
        <Stack spacing={1} sx={{ px: 3, py: 2 }}>
          <DashboardTypography variant="body2">{t("dashboard", "assistant.recovery.interrupted")}</DashboardTypography>
          <DashboardButton variant="outlined" onClick={resume}>
            {t("dashboard", "assistant.recovery.resume")}
          </DashboardButton>
        </Stack>
      )}
      <Stack
        ref={scrollContainer}
        onScroll={(event) => {
          const container = event.currentTarget;
          followLatest.current = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        }}
        sx={{ flex: 1, overflowY: "auto", px: 2, py: messages.length ? 2 : 0 }}
      >
        <Stack sx={{ width: "100%", mt: "auto" }}>
          {messages.length === 0 ? (
            <Stack spacing={3}>
              <AssistantBubble>
                <AssistantBubbleText>
                  <DashboardTypography
                    variant="body2"
                    component="h2"
                    vibe="text-neutral-intense"
                    sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
                  >
                    {welcome?.title ?? t("dashboard", "assistant.welcome.title")}
                  </DashboardTypography>
                  <DashboardTypography variant="body2" vibe="text-neutral-intense">
                    {welcome?.description ?? t("dashboard", "assistant.welcome.description")}
                  </DashboardTypography>
                </AssistantBubbleText>
              </AssistantBubble>
              <Stack direction="column" alignItems="flex-end" gap={1.5} sx={{ width: "100%", py: 1 }}>
                {suggestions.map((suggestion) => (
                  <DashboardButton
                    key={suggestion}
                    variant="outlined"
                    size="small"
                    disabled={isComposerDisabled}
                    onClick={() => {
                      if (hasSentSuggestion.current) {
                        return;
                      }
                      hasSentSuggestion.current = true;
                      reportMessageSent("suggestion");
                      submit(suggestion);
                    }}
                  >
                    {suggestion}
                  </DashboardButton>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Thread
              messages={messages}
              isBusy={isBusy}
              isComposerDisabled={isComposerDisabled}
              onAnswerApproval={answerApproval}
              onMessageSent={reportMessageSent}
            />
          )}
        </Stack>
      </Stack>
      <Stack alignItems="center" sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack spacing={0.5} sx={{ width: "100%", mx: "auto" }}>
          {errorNotice ??
            (error && (
              <DashboardTypography role="alert" variant="body2" vibe="text-danger-intense" sx={{ pb: 1 }}>
                {t("dashboard", "assistant.error")}
              </DashboardTypography>
            ))}
          {isHandedOff && !isBusy ? (
            <Stack spacing={1.5} alignItems="center" data-test={`${feature}-handed-off`} sx={{ pb: 1 }}>
              <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ textAlign: "center" }}>
                {t("dashboard", "assistant.handoff.closed")}
              </DashboardTypography>
              {onStartNew && (
                <DashboardButton variant="outlined" size="small" onClick={onStartNew}>
                  {t("dashboard", "assistant.recovery.new")}
                </DashboardButton>
              )}
            </Stack>
          ) : (
            <>
              {composerAccessory}
              <Composer
                value={input}
                isDisabled={isComposerDisabled}
                onStop={
                  canStop
                    ? () => {
                        void stop();
                      }
                    : undefined
                }
                onChange={setInput}
                onSubmit={() => {
                  reportMessageSent("typed");
                  submit();
                }}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
